import { useEffect, useState } from 'react'
import { Calendar, Fuel, Car, User, UserCog, Gauge, Droplet, Coins, MessageSquare } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'

interface Deposito {
  pracod: string
  pranom: string
  pravel: string
  comest: number
  custoMedio: number
  saldoAtual: number
}

const hoje = () => new Date().toISOString().slice(0, 10)
const agora = () => new Date().toTimeString().slice(0, 5)

// Largura proporcional ao nº real de caracteres que o campo comporta (mesmo
// princípio usado pra texto, só que com um fator menor — dígito é mais estreito
// que letra). Ex: Litros = numeric(8,3) → "12345,678" = 9 caracteres.
export const larguraNumero = (caracteres: number) => Math.max(70, caracteres * 9 + 24)

// Linha "ícone + rótulo à esquerda / campo à direita" — mesmo esqueleto do
// ModalRecapagemPneu, com um ícone a mais pra dar identidade visual a cada campo.
export function Linha({ label, icon: Icon, children }: { label: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '160px 1fr', alignItems: 'center', gap: '0.8rem',
      padding: '0.65rem 0', borderBottom: '1px solid #eef1f5',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 26, height: 26, borderRadius: 7, background: '#eff6ff', color: '#1a6ea8', flexShrink: 0,
        }}>
          <Icon size={14} />
        </span>
        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#57606f', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          {label}
        </span>
      </div>
      <div className="field-cad" style={{ margin: 0 }}>{children}</div>
    </div>
  )
}

// Caixinha de valor calculado/somente-leitura — usada pra Hod. Anterior/Posterior,
// V. Unitário e Total. `destaque` deixa o resultado principal (Total) mais vistoso.
export function CampoCalc({ rotulo, valor, largura, destaque }: { rotulo: string; valor: string; largura?: number; destaque?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
      <span style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.03em' }}>{rotulo}</span>
      <div style={{
        width: largura, padding: '0.4rem 0.7rem', borderRadius: '0.5rem', textAlign: destaque ? 'left' : 'center',
        background: destaque ? '#eff6ff' : '#f8fafc',
        border: `1px solid ${destaque ? '#bfdbfe' : '#e5e7eb'}`,
        color: destaque ? '#1a6ea8' : '#475569',
        fontWeight: destaque ? 800 : 600,
        fontSize: destaque ? '0.95rem' : '0.85rem',
      }}>
        {valor}
      </div>
    </div>
  )
}

// Com arla: Lançamento ARLA Depósito (WDPS062 → TDPS065 → TDPS066), só depósito de ARLA32 e no máximo 300 L.
const LIMITE_LITROS_ARLA_DEPOSITO = 300

export default function LancamentoDepositos({ arla = false }: { arla?: boolean }) {
  const { empresaCod } = useEmpresa()
  const [depositos, setDepositos] = useState<Deposito[]>([])

  const [data, setData] = useState(hoje())
  const [horario, setHorario] = useState(agora())
  const [pracod, setPracod] = useState('')
  const [veicod, setVeicod] = useState('')
  const [veipla, setVeipla] = useState('')
  const [motcod, setMotcod] = useState('')
  const [motnome, setMotnome] = useState('')
  const [abastecedorCod, setAbastecedorCod] = useState('')
  const [abastecedorNome, setAbastecedorNome] = useState('')
  const [hodometro, setHodometro] = useState('')
  const [litros, setLitros] = useState('')
  const [observacao, setObservacao] = useState('')

  const [hodAnterior, setHodAnterior] = useState<number | null>(null)
  const [hodPosterior, setHodPosterior] = useState<number | null>(null)
  const [capacidadeTanque, setCapacidadeTanque] = useState(0)

  const [salvando, setSalvando] = useState(false)
  const [msg, setMsg] = useState<{ texto: string; ok: boolean } | null>(null)

  const depositoAtual = depositos.find(d => d.pracod === pracod)
  const custoMedio = Number(depositoAtual?.custoMedio) || 0
  const saldoAtual = depositoAtual ? Number(depositoAtual.saldoAtual) || 0 : null
  const saldoZerado = saldoAtual != null && saldoAtual <= 0
  const valorEstimado = custoMedio * (Number(litros) || 0)
  const limiteLitros = arla ? LIMITE_LITROS_ARLA_DEPOSITO : capacidadeTanque
  const excedeTanque = limiteLitros > 0 && Number(litros) > limiteLitros

  useEffect(() => {
    // ARLA32 tem tela própria: cada modo só lista os seus depósitos.
    api.get('/combustivel/depositos').then(r => {
      const lista: Deposito[] = r.data.filter((d: Deposito) => (d.pravel === 'ARLA32') === arla)
      setDepositos(lista)
      if (arla && lista.length === 1) setPracod(lista[0].pracod)
    })
  }, [arla])

  useEffect(() => {
    if (!veicod || !data) { setHodAnterior(null); setHodPosterior(null); setCapacidadeTanque(0); return }
    api.get('/combustivel/veiculo-contexto', { params: { veicod, data, empresa: empresaCod ?? 1 } }).then(r => {
      setHodAnterior(r.data.hodAnterior)
      setHodPosterior(r.data.hodPosterior)
      setCapacidadeTanque(Number(r.data.capacidadeTanque) || 0)
    })
  }, [veicod, data, empresaCod])

  function limparFormulario() {
    setData(hoje())
    setHorario(agora())
    setPracod(arla && depositos.length === 1 ? depositos[0].pracod : '')
    setVeicod(''); setVeipla('')
    setMotcod(''); setMotnome('')
    setAbastecedorCod(''); setAbastecedorNome('')
    setHodometro('')
    setLitros('')
    setObservacao('')
  }

  async function salvar() {
    setMsg(null)
    if (!pracod || !veicod || !motcod || !litros) {
      setMsg({ texto: 'Preencha depósito, veículo, motorista e litros.', ok: false })
      return
    }
    if (saldoZerado) {
      setMsg({ texto: `Depósito ${pracod} está com saldo zerado — não é possível lançar.`, ok: false })
      return
    }
    setSalvando(true)
    try {
      const r = await api.post('/combustivel/lancamentos', {
        data, horario, pracod, veicod, motcod,
        abastecedorCod: abastecedorCod || null,
        hodometro: Number(hodometro) || 0,
        litros: Number(litros),
        observacao: observacao || null,
        empresa: empresaCod ?? 1, arla,
      })
      setMsg({ texto: `Lançamento ${r.data.dpsnum} salvo — valor R$ ${r.data.valorTotal.toFixed(2)}.`, ok: true })
      limparFormulario()
    } catch (e: any) {
      setMsg({ texto: e?.response?.data?.error || 'Erro ao salvar lançamento.', ok: false })
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="modal-box" style={{ width: '100%', maxWidth: 640, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
      <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
          <span style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)',
          }}>
            <Fuel size={18} color="#fff" />
          </span>
          <div>
            <h2>{arla ? 'Lançamento ARLA Depósito' : 'Lançamento Depósitos'}</h2>
            <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>{arla ? 'Consumo de ARLA32 do depósito' : 'Abastecimento interno por veículo'}</div>
          </div>
        </div>
      </div>

      <div className="modal-corpo">
        <Linha label="Data" icon={Calendar}>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <input type="date" value={data} max={arla ? hoje() : undefined} onChange={e => setData(e.target.value)} style={{ maxWidth: 170 }} />
            <input type="time" value={horario} onChange={e => setHorario(e.target.value)} style={{ maxWidth: 120 }} />
          </div>
        </Linha>

        <Linha label="Depósito" icon={Fuel}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <select value={pracod} onChange={e => setPracod(e.target.value)} style={{ maxWidth: 260 }}>
              <option value="">Selecione...</option>
              {depositos.map(d => (
                <option key={d.pracod} value={d.pracod}>{d.pracod} — {d.pranom}</option>
              ))}
            </select>
            {depositoAtual && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 700, color: '#1a6ea8', background: '#eff6ff',
                border: '1px solid #bfdbfe', borderRadius: 999, padding: '0.2rem 0.7rem',
              }}>
                ⛽ {depositoAtual.pravel}
              </span>
            )}
            {depositoAtual && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 700,
                color: saldoZerado ? '#991b1b' : '#065f46',
                background: saldoZerado ? '#fee2e2' : '#d1fae5',
                border: `1px solid ${saldoZerado ? '#fecaca' : '#a7f3d0'}`,
                borderRadius: 999, padding: '0.2rem 0.7rem',
              }}>
                saldo {Number(depositoAtual.saldoAtual).toFixed(3)}L
              </span>
            )}
          </div>
          {saldoZerado && (
            <div style={{
              marginTop: '0.5rem', fontSize: '0.78rem', fontWeight: 700, color: '#991b1b',
              background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.5rem 0.75rem',
            }}>
              ⚠ Este depósito está com saldo zerado — não é possível lançar abastecimento até repor o estoque.
            </div>
          )}
        </Linha>

        <Linha label="Veículo" icon={Car}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{ maxWidth: 140 }}>
              <LookupSelect
                endpoint="/erp/veiculos"
                codKey="veicod"
                descKey="veipla"
                tagKey="veicod"
                codValue={veicod}
                descValue={veicod}
                onSelect={(cod, _desc, item) => { setVeicod(cod); setVeipla(item?.veipla ?? '') }}
                placeholder="Buscar veículo..."
              />
            </div>
            {veipla && <span style={{ fontSize: '0.82rem', color: '#57606f' }}>placa {veipla}</span>}
          </div>
        </Linha>

        <Linha label="Motorista" icon={User}>
          <LookupSelect
            endpoint="/combustivel/motoristas"
            codKey="motcod"
            descKey="motnome"
            tagKey="motcod"
            codValue={motcod}
            descValue={motnome}
            onSelect={(cod, desc) => { setMotcod(cod); setMotnome(desc) }}
            placeholder="Buscar motorista..."
            listWidth={320}
          />
        </Linha>

        <Linha label="Abastecedor" icon={UserCog}>
          <LookupSelect
            endpoint="/combustivel/motoristas"
            codKey="motcod"
            descKey="motnome"
            tagKey="motcod"
            codValue={abastecedorCod}
            descValue={abastecedorNome}
            onSelect={(cod, desc) => { setAbastecedorCod(cod); setAbastecedorNome(desc) }}
            placeholder="Buscar abastecedor..."
            listWidth={320}
          />
        </Linha>

        <Linha label="Hodômetro" icon={Gauge}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
            <input type="number" value={hodometro} onChange={e => setHodometro(e.target.value)} style={{ width: larguraNumero(7) }} />
            <CampoCalc rotulo="ANTERIOR" valor={hodAnterior != null ? String(hodAnterior) : '—'} largura={larguraNumero(7)} />
            <CampoCalc rotulo="POSTERIOR" valor={hodPosterior != null ? String(hodPosterior) : '—'} largura={larguraNumero(7)} />
          </div>
        </Linha>

        <Linha label="Litros" icon={Droplet}>
          <div>
            <input
              type="number" step="0.001" value={litros} onChange={e => setLitros(e.target.value)}
              style={{ width: larguraNumero(9), borderColor: excedeTanque ? '#dc2626' : undefined }}
            />
            {limiteLitros > 0 && (
              <div style={{ fontSize: '0.72rem', marginTop: 4, color: excedeTanque ? '#dc2626' : '#94a3b8', fontWeight: excedeTanque ? 700 : 400 }}>
                {arla
                  ? (excedeTanque ? `Acima do máximo de ${limiteLitros}L de ARLA` : `Máximo: ${limiteLitros}L`)
                  : (excedeTanque ? `Acima da capacidade do tanque (${capacidadeTanque}L)` : `Capacidade do tanque: ${capacidadeTanque}L`)}
              </div>
            )}
          </div>
        </Linha>

        <Linha label="Valor" icon={Coins}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
            <CampoCalc rotulo="V. UNITÁRIO" valor={custoMedio ? custoMedio.toFixed(4) : '—'} largura={larguraNumero(8)} />
            <CampoCalc rotulo="TOTAL" valor={custoMedio ? `R$ ${valorEstimado.toFixed(2)}` : '—'} largura={larguraNumero(12)} destaque />
          </div>
        </Linha>

        {/* Observação oculta por enquanto */}
        {false && (
          <Linha label="Observação" icon={MessageSquare}>
            <input type="text" maxLength={30} value={observacao} onChange={e => setObservacao(e.target.value)} style={{ width: Math.max(90, 30 * 7 + 30) }} />
          </Linha>
        )}

        {msg && (
          <div style={{
            marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8,
            background: msg.ok ? '#d1fae5' : '#fee2e2',
            color: msg.ok ? '#065f46' : '#991b1b',
            border: `1px solid ${msg.ok ? '#a7f3d0' : '#fecaca'}`,
          }}>
            {msg.texto}
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={limparFormulario} disabled={salvando}>Limpar</button>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando || saldoZerado}>
          {salvando ? 'Salvando...' : 'Salvar lançamento'}
        </button>
      </div>
    </div>
  )
}
